import React, { useState } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { Fetcher } from '@this/src/util';
import { Button } from '@this/shared/ui/inputs/button';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { Modal } from '@this/shared/ui/feedbacks/modal';
import { ConfirmModal } from '@this/shared/ui/feedbacks/modal/confirm_modal/confirm_modal';
import { colors } from '@this/shared/ui/theme/tokens/primitive-tokens/colors';
import moment from 'moment';
import { OnlineApplicationView } from './shared/online_application_view';
import { ExpirationMessage } from './shared/expiration_message';
import { useApplicationData } from './shared/use_application_data';
import type { FormInputState } from './shared/types';
import { API_ENDPOINTS, ERROR_MESSAGES } from './shared/types';
import { onlineApplicationStyles } from './shared/styles';

const OnlineApplicationConfirm = () => {
  const history = useHistory();
  const location = useLocation<FormInputState>();
  const { token } = useParams<{ token: string }>();
  const {
    loading,
    application,
    contactName: defaultContactName,
    contactEmail: defaultContactEmail,
    contactTel: defaultContactTel,
    startDesiredMonth: defaultStartDesiredMonth,
    errorMessage,
    setErrorMessage
  } = useApplicationData({ token });

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  // location.stateから传わった値を優先的に使用
  const contactNameKana = location.state?.contactNameKana || '';
  const contactName = location.state?.contactName || defaultContactName;
  const contactEmail = location.state?.contactEmail || defaultContactEmail;
  const contactTel = location.state?.contactTel || defaultContactTel;
  const startDesiredMonthRaw = location.state?.startDesiredMonth || defaultStartDesiredMonth;
  const submitterCompanyName = location.state?.submitterCompanyName || '';
  const submitterDepartment = location.state?.submitterDepartment || '';
  const submitterName = location.state?.submitterName || '';
  const submitterEmail = location.state?.submitterEmail || '';
  // 返金口座情報
  const bankName = location.state?.bankName || '';
  const branchName = location.state?.branchName || '';
  const accountType = location.state?.accountType || '';
  const accountHolderName = location.state?.accountHolderName || '';
  const accountNumber = location.state?.accountNumber || '';

  // 開始希望月をYYYY/MM形式に変換
  const startDesiredMonth = startDesiredMonthRaw
    ? moment(startDesiredMonthRaw, 'YYYY年M月').format('YYYY/MM')
    : '';

  const handleSubmit = async () => {
    setShowConfirmModal(false);

    try {
      const submitData = {
        online_application: {
          start_desired_month: startDesiredMonthRaw
            ? moment(startDesiredMonthRaw, 'YYYY年M月').format('YYYY-MM-DD')
            : null,
          submitter_company_name: submitterCompanyName,
          submitter_department: submitterDepartment,
          submitter_name: submitterName,
          submitter_email: submitterEmail,
          companies: [
            {
              id: application!.companies[0].id,
              contact_name_kana: contactNameKana,
              contact_name: contactName,
              contact_email: contactEmail,
              contact_tel: contactTel
            }
          ],
          billing: {
            bank_name: bankName,
            branch_name: branchName,
            account_type: accountType,
            account_holder_name: accountHolderName,
            account_number: accountNumber
          }
        }
      };

      const response = await Fetcher.put<{ success: boolean; message: string }>(
        API_ENDPOINTS.USER.UPDATE(token),
        submitData
      );

      if (response.success) {
        history.push(`/online_applications/complete/${token}`);
      } else {
        setErrorMessage(response.message || ERROR_MESSAGES.FETCH_FAILED);
        setShowErrorModal(true);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.error || ERROR_MESSAGES.FETCH_FAILED);
      setShowErrorModal(true);
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (!application) {
    return (
      <div style={{ ...onlineApplicationStyles.container.main, backgroundColor: 'none' }}>
        <div style={onlineApplicationStyles.message.error.container}>
          <p style={onlineApplicationStyles.message.error.text}>
            {errorMessage && (
              <>
                {errorMessage.split('\n').map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    {index < errorMessage.split('\n').length - 1 && <br />}
                  </React.Fragment>
                ))}
                <br />
                {ERROR_MESSAGES.CONTACT_SUPPORT}
              </>
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={onlineApplicationStyles.container.main}>
      <h1 style={onlineApplicationStyles.heading.primary}>入力内容の確認</h1>

      <ExpirationMessage expirationDate={application.expirationDate} buttonText="申し込む" />

      <OnlineApplicationView
        application={application}
        contactName={contactName}
        contactEmail={contactEmail}
        contactTel={contactTel}
        startDesiredMonth={startDesiredMonth}
        submitterCompanyName={submitterCompanyName}
        submitterDepartment={submitterDepartment}
        submitterName={submitterName}
        submitterEmail={submitterEmail}
        bankName={bankName}
        branchName={branchName}
        accountType={accountType}
        accountHolderName={accountHolderName}
        accountNumber={accountNumber}
      />

      {/* ボタンセクション */}
      <div style={onlineApplicationStyles.buttonSection.base}>
        <h3 style={onlineApplicationStyles.heading.secondary}>本申込書発行日</h3>
        <p style={{ fontSize: '18px', marginBottom: '24px' }}>{moment().format('YYYY-MM-DD HH:mm')}</p>
        <p style={onlineApplicationStyles.text.note}>
          申込完了後、担当者メールアドレスに設定した宛先へ送信完了メールが通知されます。
          <br />
          24時間経過しても通知が来ない場合は弊社担当者までお知らせください。
        </p>

        <div style={{ ...onlineApplicationStyles.button.group, justifyContent: 'center', marginTop: '24px' }}>
          <Button
            color="sub"
            onClick={() => {
              // 現在の入力値をフォーム画面に渡して戻る
              history.push(`/online_applications/token/${token}`, {
                contactNameKana,
                contactName,
                contactEmail,
                contactTel,
                startDesiredMonth,
                submitterCompanyName,
                submitterDepartment,
                submitterName,
                submitterEmail,
                bankName,
                branchName,
                accountType,
                accountHolderName,
                accountNumber
              });
            }}
          >
            戻る
          </Button>
          <Button color="primary" onClick={() => setShowConfirmModal(true)}>
            申し込む
          </Button>
        </div>
      </div>

      {/* 確認モーダル */}
      <ConfirmModal
        open={showConfirmModal}
        description="申込内容をご確認いただき、よろしければ「申込む」ボタンをクリックしてください。"
        color="primary"
        submitButtonLabel="申込む"
        onSubmit={handleSubmit}
        onCancel={() => setShowConfirmModal(false)}
      />

      {/* エラーモーダル */}
      <Modal open={showErrorModal} onClose={() => setShowErrorModal(false)} size="small">
        <div style={onlineApplicationStyles.modal.content}>
          <p style={{ ...onlineApplicationStyles.modal.title, color: colors.red[600] }}>エラー</p>
          <p style={onlineApplicationStyles.modal.message}>
            {errorMessage && (
              <>
                {errorMessage.split('\n').map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    {index < errorMessage.split('\n').length - 1 && <br />}
                  </React.Fragment>
                ))}
                <br />
                {ERROR_MESSAGES.CONTACT_SUPPORT}
              </>
            )}
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default OnlineApplicationConfirm;
